import { EventInput } from '@fullcalendar/core';
import { DateTime } from 'luxon';
import { CalendarEventDto } from '../dto';
import { eventStatusColors } from './event-color.utils';

/** Fallback event length (30 min) when neither `end` nor a session duration is known. */
const DEFAULT_EVENT_DURATION_SECONDS = 1800;

/**
 * Converts a lean {@link CalendarEventDto} (the `view=calendar` projection) into a
 * read-only FullCalendar {@link EventInput}.
 *
 * The DTO is flat — `title` and `duration` are already resolved server-side, so there's
 * no nested session/exercise/profile graph to read. Notes:
 * - `start`/`end` arrive as ISO strings.
 * - An event anchored at midnight has no explicit scheduled time yet, so it is treated
 *   as all-day (it would otherwise be hidden below `slotMinTime`).
 * - `extendedProps` carries the discriminator `type` plus the ids needed to navigate
 *   from a clicked event back to its patient / assignment.
 * - `userFullName` and `duration` feed the hover preview.
 */
export function calendarEventToCalendarEvent(dto: CalendarEventDto): EventInput {
  const start = DateTime.fromISO(dto.start as unknown as string);

  const isAtMidnight = start.hour === 0 && start.minute === 0 && start.second === 0;
  const allDay = dto.allDay || isAtMidnight;

  const durationSeconds = dto.duration || DEFAULT_EVENT_DURATION_SECONDS;
  const end = dto.end
    ? DateTime.fromISO(dto.end as unknown as string).toJSDate()
    : start.plus({ seconds: durationSeconds }).toJSDate();

  const { background, border, text } = eventStatusColors(dto.status);

  return {
    id: `${dto.id}`,
    title: dto.title,
    start: start.toJSDate(),
    end,
    allDay,
    editable: false,
    overlap: false,
    backgroundColor: background,
    borderColor: border,
    textColor: text,
    extendedProps: {
      type: dto.type,
      status: dto.status,
      userId: dto.userId ?? null,
      assignmentId: dto.assignmentId ?? null,
      userFullName: dto.userFullName ?? null,
      duration: durationSeconds,
    },
  } as EventInput;
}
